/* Lead Planner tab — inline-editable table of lead sources.
 * Same math as Tracy's spreadsheet: closings/wk = leads/wk ÷ leads-per-contract,
 * weekly budget = cost/lead × leads/wk. Everything recomputes live as you type. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Save, Download } from "lucide-react";
import { api } from "@/lib/api";
import { downloadCsv } from "./reports";

const money = (n) => `$${(+n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const num = (n) => (+n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

function calc(row) {
  const closingsWk = row.leads_per_contract > 0 ? row.leads_per_week / row.leads_per_contract : 0;
  const closingsMo = closingsWk * 52 / 12;
  const weeklyBudget = row.cost_per_lead * row.leads_per_week;
  const monthlyBudget = weeklyBudget * 52 / 12;
  const annualBudget = weeklyBudget * 52;
  return { closingsWk, closingsMo, weeklyBudget, monthlyBudget, annualBudget };
}

export default function LeadPlannerTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/command-center/lead-sources");
      setRows(data.items || []);
    } catch {
      toast.error("Could not load lead sources.");
    }
  };
  useEffect(() => { load(); }, []);

  const patch = (id, field, val) => {
    setRows((prev) => prev.map((r) => r.id === id ? { ...r, [field]: val, _dirty: true } : r));
  };

  const save = async (row) => {
    setBusy(true);
    try {
      await api.patch(`/command-center/lead-sources/${row.id}`, {
        name: row.name,
        cost_per_lead: +row.cost_per_lead,
        leads_per_week: +row.leads_per_week,
        leads_per_contract: Math.max(1, +row.leads_per_contract),
        active: !!row.active,
      });
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, _dirty: false } : r));
      toast.success("Saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const addRow = async () => {
    try {
      const { data } = await api.post("/command-center/lead-sources", {
        name: "New Source", cost_per_lead: 0, leads_per_week: 0, leads_per_contract: 20, active: true,
      });
      setRows((prev) => [...prev, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Add failed.");
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this lead source?")) return;
    try {
      await api.delete(`/command-center/lead-sources/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch {
      toast.error("Delete failed.");
    }
  };

  // Roll-up
  const totals = rows.filter((r) => r.active).reduce((acc, r) => {
    const c = calc(r);
    acc.closingsMo += c.closingsMo;
    acc.monthlyBudget += c.monthlyBudget;
    acc.annualBudget += c.annualBudget;
    return acc;
  }, { closingsMo: 0, monthlyBudget: 0, annualBudget: 0 });

  return (
    <div className="space-y-4" data-testid="lead-planner-tab">
      <div className="card overflow-x-auto">
        <table className="w-full text-sm" data-testid="lead-planner-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--ink-3)] border-b border-[var(--line)]">
              <th className="p-2 min-w-[160px]">Lead Source</th>
              <th className="p-2">Cost/lead ($)</th>
              <th className="p-2">Leads/wk</th>
              <th className="p-2">Leads/contract</th>
              <th className="p-2">Closings/mo</th>
              <th className="p-2">Weekly budget</th>
              <th className="p-2">Monthly budget</th>
              <th className="p-2">Annual budget</th>
              <th className="p-2">Active</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-[var(--ink-3)] p-6">
                No lead sources yet. Click <strong>Add lead source</strong> below or hit
                <strong> Load Tracy&apos;s template</strong> at the top of the page.
              </td></tr>
            )}
            {rows.map((r) => {
              const c = calc({
                ...r,
                cost_per_lead: +r.cost_per_lead || 0,
                leads_per_week: +r.leads_per_week || 0,
                leads_per_contract: +r.leads_per_contract || 1,
              });
              return (
                <tr key={r.id} className={`border-b border-[var(--line)] ${r._dirty ? "bg-amber-50" : ""}`}
                    data-testid={`lead-source-row-${r.id}`}>
                  <td className="p-2">
                    <input className="form-input" value={r.name}
                           onChange={(e) => patch(r.id, "name", e.target.value)} />
                  </td>
                  <td className="p-2">
                    <input type="number" className="form-input" value={r.cost_per_lead} min={0} step={0.5}
                           onChange={(e) => patch(r.id, "cost_per_lead", e.target.value)}
                           style={{ width: 90 }} />
                  </td>
                  <td className="p-2">
                    <input type="number" className="form-input" value={r.leads_per_week} min={0} step={0.5}
                           onChange={(e) => patch(r.id, "leads_per_week", e.target.value)}
                           style={{ width: 80 }} />
                  </td>
                  <td className="p-2">
                    <input type="number" className="form-input" value={r.leads_per_contract} min={1} step={1}
                           onChange={(e) => patch(r.id, "leads_per_contract", e.target.value)}
                           style={{ width: 80 }} />
                  </td>
                  <td className="p-2 font-semibold text-[var(--ink)]">{num(c.closingsMo)}</td>
                  <td className="p-2">{money(c.weeklyBudget)}</td>
                  <td className="p-2 font-semibold">{money(c.monthlyBudget)}</td>
                  <td className="p-2 text-[var(--ink-2)]">{money(c.annualBudget)}</td>
                  <td className="p-2">
                    <input type="checkbox" checked={!!r.active}
                           onChange={(e) => patch(r.id, "active", e.target.checked)} />
                  </td>
                  <td className="p-2 flex gap-1">
                    {r._dirty && (
                      <button type="button" onClick={() => save(r)} disabled={busy}
                              className="btn-outline text-[var(--primary)] p-2" title="Save">
                        <Save size={14} />
                      </button>
                    )}
                    <button type="button" onClick={() => removeRow(r.id)}
                            className="btn-outline text-[var(--warn)] p-2" title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-[var(--muted)]">
              <tr className="border-t-2 border-[var(--line)]">
                <td className="p-2 font-bold" colSpan={4}>Totals (active only)</td>
                <td className="p-2 font-display font-black">{num(totals.closingsMo)}</td>
                <td className="p-2"></td>
                <td className="p-2 font-display font-black">{money(totals.monthlyBudget)}</td>
                <td className="p-2 font-display font-black text-[var(--ink-2)]">{money(totals.annualBudget)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <button type="button" onClick={addRow} className="btn-outline" data-testid="lead-planner-add">
        <Plus size={14} /> Add lead source
      </button>
      <button type="button" onClick={() => downloadCsv("lead-sources")}
              className="btn-outline ml-2" data-testid="lead-planner-download">
        <Download size={14} /> Download CSV
      </button>
    </div>
  );
}
